import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { 
  Building2, Users, Package, Search, Eye, RefreshCw, Loader2,
  ChevronLeft, ChevronRight, AlertTriangle, CreditCard, Grid3X3, User, Trash2, Download, Coins,
  ArrowUp, ArrowDown, ArrowUpDown, Receipt, X, Clock, Check, Plus, BarChartHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import CreateOrganizationModal from './CreateOrganizationModal';
import GridSelectionModal from './GridSelectionModal';
import BillingAuditLog from '../billing/BillingAuditLog';

const AdminOrganizationsContent = () => {
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const { startImpersonation } = useAuth();
  const colors = getThemeColors();
  
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Impersonate modal state
  const [impersonateModal, setImpersonateModal] = useState(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Detail modal
  const [detailModal, setDetailModal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orgDetail, setOrgDetail] = useState(null);
  
  // Grid/Plan modal
  const [showGridModal, setShowGridModal] = useState(false);
  const [gridModalOrg, setGridModalOrg] = useState(null);
  
  // Billing history modal
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingModalOrg, setBillingModalOrg] = useState(null);
  
  // Close org modal
  const [closeOrgModal, setCloseOrgModal] = useState(null);
  const [closeConfirmName, setCloseConfirmName] = useState('');
  const [closingOrg, setClosingOrg] = useState(false);
  
  // Force delete modal
  const [forceDeleteModal, setForceDeleteModal] = useState(null);
  const [forceDeleteConfirmName, setForceDeleteConfirmName] = useState('');
  const [forceDeleting, setForceDeleting] = useState(false);
  const [forceDeleteResults, setForceDeleteResults] = useState(null);
  
  // Create organization modal
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  
  // Add tokens modal
  const [addTokensModal, setAddTokensModal] = useState(null);
  const [tokenAmount, setTokenAmount] = useState('100000');
  const [tokenReason, setTokenReason] = useState('');
  const [addingTokens, setAddingTokens] = useState(false);
  const [orgTokenStatus, setOrgTokenStatus] = useState(null);

  const limit = 20;

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (search) params.append('search', search);
      if (planFilter !== 'all') params.append('plan_tier', planFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await api.get(`/api/admin/metrics/organizations?${params}`);
      setOrganizations(res.data.organizations || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [page, search, planFilter, statusFilter]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchOrganizations();
  };

  const openImpersonateModal = async (org) => {
    setImpersonateModal(org);
    setSelectedUserId(null);
    setOrgUsers([]);
    setLoadingUsers(true);
    
    try {
      const res = await api.get(`/api/admin/metrics/organizations/${org.organization_id}/users`);
      setOrgUsers(res.data.users || []);
      if (res.data.users?.length > 0) {
        const owner = res.data.users.find(u => u.team_role === 'owner');
        setSelectedUserId(owner?.user_id || res.data.users[0].user_id);
      }
    } catch (err) {
      toast.error('Failed to load organization users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateModal || !selectedUserId) return;
    
    setImpersonateLoading(true);
    try {
      const res = await api.post(
        `/api/admin/metrics/organizations/${impersonateModal.organization_id}/impersonate`,
        { user_id: selectedUserId }
      );
      
      if (res.data.success) {
        startImpersonation(
          res.data.impersonating,
          res.data.organization,
          res.data.user
        );
        
        toast.success(`Now viewing as ${res.data.impersonating.user_name}`);
        setImpersonateModal(null);
        navigate('/products');
      }
    } catch (err) {
      toast.error('Failed to impersonate organization');
    } finally {
      setImpersonateLoading(false);
    }
  };

  const handleViewDetail = async (org) => {
    setDetailModal(org);
    setDetailLoading(true);
    try {
      const res = await api.get(`/api/admin/metrics/organizations/${org.organization_id}`);
      setOrgDetail(res.data);
    } catch (err) {
      toast.error('Failed to load organization details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAdminCloseOrg = async () => {
    if (!closeOrgModal) return;
    
    if (closeConfirmName.toLowerCase().trim() !== closeOrgModal.name?.toLowerCase().trim()) {
      toast.error('Organization name does not match');
      return;
    }
    
    setClosingOrg(true);
    try {
      await api.post(`/api/admin/metrics/organizations/${closeOrgModal.organization_id}/close`, {
        confirm_name: closeConfirmName
      });
      toast.success('Organization scheduled for closure');
      setCloseOrgModal(null);
      setCloseConfirmName('');
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close organization');
    } finally {
      setClosingOrg(false);
    }
  };

  const handleForceDelete = async () => {
    if (!forceDeleteModal) return;
    
    if (forceDeleteConfirmName.toLowerCase().trim() !== forceDeleteModal.name?.toLowerCase().trim()) {
      toast.error('Organization name does not match');
      return;
    }
    
    setForceDeleting(true);
    setForceDeleteResults(null);
    
    try {
      const response = await api.delete(`/api/admin/metrics/organizations/${forceDeleteModal.organization_id}/force`, {
        data: { confirm_name: forceDeleteConfirmName }
      });
      
      // Store results for display
      setForceDeleteResults(response.data);
      
      // Refresh the list in background
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete organization');
      setForceDeleteResults({
        success: false,
        error: err.response?.data?.detail || 'Failed to delete organization'
      });
    } finally {
      setForceDeleting(false);
    }
  };
  
  const closeForceDeleteModal = () => {
    setForceDeleteModal(null);
    setForceDeleteConfirmName('');
    setForceDeleteResults(null);
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'not_found':
        return <span className="w-4 h-4 text-gray-400">○</span>;
      case 'error':
        return <X className="w-4 h-4 text-red-500" />;
      default:
        return <span className="w-4 h-4 text-gray-400">?</span>;
    }
  };

  const openAddTokensModal = async (org) => {
    setAddTokensModal(org);
    setTokenAmount('100000');
    setTokenReason('');
    setOrgTokenStatus(null);
    
    try {
      const res = await api.get(`/api/admin/metrics/organizations/${org.organization_id}/token-status`);
      setOrgTokenStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch token status');
    }
  };

  const handleAddTokens = async () => {
    if (!addTokensModal || !tokenAmount) return;
    
    setAddingTokens(true);
    try {
      await api.post(`/api/admin/metrics/organizations/${addTokensModal.organization_id}/add-tokens`, {
        amount: parseInt(tokenAmount),
        reason: tokenReason || 'Admin grant'
      });
      toast.success(`Added ${parseInt(tokenAmount).toLocaleString()} tokens`);
      setAddTokensModal(null);
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add tokens');
    } finally {
      setAddingTokens(false);
    }
  };

  const handleGridUpdated = () => {
    fetchOrganizations();
    setShowGridModal(false);
    setGridModalOrg(null);
  };

  // Sort organizations
  const sortedOrganizations = useMemo(() => {
    const sorted = [...organizations].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'plan':
          aVal = a.plan_tier || '';
          bVal = b.plan_tier || '';
          break;
        case 'status':
          aVal = a.subscription_status || '';
          bVal = b.subscription_status || '';
          break;
        case 'users':
          aVal = a.user_count || 0;
          bVal = b.user_count || 0;
          break;
        case 'products':
          aVal = a.product_count || 0;
          bVal = b.product_count || 0;
          break;
        case 'created':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [organizations, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (org) => {
    if (org.closure_status === 'closing') {
      return <Badge className="bg-orange-100 text-orange-700">Closing</Badge>;
    }
    if (org.subscription_status === 'past_due') {
      return <Badge className="bg-red-100 text-red-700">Past Due</Badge>;
    }
    if (org.subscription_status === 'trialing') {
      return <Badge className="bg-yellow-100 text-yellow-700">Trial</Badge>;
    }
    if (org.plan_tier === 'paid') {
      return <Badge className="bg-green-100 text-green-700">Active</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700">Free</Badge>;
  };

  const openBillingModal = (org) => {
    setBillingModalOrg(org);
    setShowBillingModal(true);
  };

  const closeBillingModal = () => {
    setShowBillingModal(false);
    setBillingModalOrg(null);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <Card className={cn("p-4", colors.bgCard)}>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
              <Input
                placeholder="Search by name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn("pl-10", colors.inputBg)}
              />
            </div>
          </div>
          
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className={cn("w-[140px]", colors.inputBg)}>
              <SelectValue placeholder="Plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={cn("w-[140px]", colors.inputBg)}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closing">Closing</SelectItem>
              <SelectItem value="past_due">Past Due</SelectItem>
            </SelectContent>
          </Select>
          
          <Button type="submit" variant="outline">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
          
          <Button type="button" variant="ghost" onClick={fetchOrganizations}>
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </Button>
          
          <Button type="button" onClick={() => setShowCreateOrgModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Create Organization
          </Button>
        </form>
      </Card>

      {/* Organizations Table */}
      <Card className={cn("overflow-hidden", colors.bgCard)}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className={colors.textMuted}>Loading organizations...</span>
          </div>
        ) : organizations.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className={cn("w-12 h-12 mx-auto mb-4 opacity-50", colors.textMuted)} />
            <p className={colors.textMuted}>No organizations found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={cn("border-b", colors.bgSecondary, colors.borderPrimary)}>
                  <tr>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">Organization{renderSortIcon('name')}</div>
                    </th>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('plan')}
                    >
                      <div className="flex items-center">Plan{renderSortIcon('plan')}</div>
                    </th>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">Status{renderSortIcon('status')}</div>
                    </th>
                    <th 
                      className={cn("text-center px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('users')}
                    >
                      <div className="flex items-center justify-center">Users{renderSortIcon('users')}</div>
                    </th>
                    <th 
                      className={cn("text-center px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('products')}
                    >
                      <div className="flex items-center justify-center">Products{renderSortIcon('products')}</div>
                    </th>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('created')}
                    >
                      <div className="flex items-center">Created{renderSortIcon('created')}</div>
                    </th>
                    <th className={cn("text-right px-4 py-3 text-xs font-medium uppercase", colors.textMuted)}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedOrganizations.map((org) => (
                    <tr key={org.organization_id} className="hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={cn("font-medium", colors.textPrimary)}>{org.name}</p>
                            {org.exclude_from_analytics && (
                              <Badge data-testid={`analytics-excluded-badge-${org.organization_id}`} variant="outline" className="text-[10px] px-1.5 py-0 text-amber-600 border-amber-300 bg-amber-50">
                                <BarChartHorizontal className="w-3 h-3 mr-0.5" />
                                Excluded
                              </Badge>
                            )}
                          </div>
                          <p className={cn("text-xs", colors.textMuted)}>{org.organization_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge 
                          variant="outline" 
                          className="capitalize cursor-pointer hover:bg-muted/50"
                          onClick={() => { setGridModalOrg(org); setShowGridModal(true); }}
                        >
                          {org.plan_tier || 'free'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(org)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{org.user_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Package className="w-4 h-4" />
                          <span>{org.product_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className={cn("text-sm", colors.textMuted)}>
                            {formatShortDate(org.created_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetail(org)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openImpersonateModal(org)}
                            title="Impersonate"
                          >
                            <User className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setGridModalOrg(org); setShowGridModal(true); }}
                            title="Grid/Plan"
                          >
                            <Grid3X3 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openBillingModal(org)}
                            title="Billing History"
                          >
                            <Receipt className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAddTokensModal(org)}
                            title="Add Tokens"
                          >
                            <Coins className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={cn("flex items-center justify-between px-4 py-3 border-t", colors.borderPrimary)}>
              <p className={cn("text-sm", colors.textMuted)}>
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className={cn("text-sm", colors.textMuted)}>
                  Page {page + 1} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Impersonate Modal */}
      <Dialog open={!!impersonateModal} onOpenChange={() => setImpersonateModal(null)}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle>Impersonate Organization</DialogTitle>
            <DialogDescription>
              Select a user to view the app as
            </DialogDescription>
          </DialogHeader>
          
          {loadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <RadioGroup value={selectedUserId} onValueChange={setSelectedUserId}>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {orgUsers.map(user => (
                  <div key={user.user_id} className={cn("flex items-center space-x-3 p-3 rounded-lg border", colors.borderPrimary)}>
                    <RadioGroupItem value={user.user_id} id={user.user_id} />
                    <Label htmlFor={user.user_id} className="flex-1 cursor-pointer">
                      <p className={cn("font-medium", colors.textPrimary)}>{user.name || user.email}</p>
                      <p className={cn("text-xs", colors.textMuted)}>{user.team_role} • {user.email}</p>
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateModal(null)}>
              Cancel
            </Button>
            <Button onClick={handleImpersonate} disabled={!selectedUserId || impersonateLoading}>
              {impersonateLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              View As User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!detailModal} onOpenChange={() => setDetailModal(null)}>
        <DialogContent className={cn("max-w-2xl max-h-[80vh] overflow-y-auto", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle>Organization Details</DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : orgDetail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={colors.textMuted}>Name</Label>
                  <p className={cn("font-medium", colors.textPrimary)}>{orgDetail.organization?.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>ID</Label>
                  <p className={cn("font-mono text-sm", colors.textPrimary)}>{orgDetail.organization?.organization_id || 'N/A'}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Plan</Label>
                  <p className={colors.textPrimary}>{orgDetail.organization?.plan_tier || 'N/A'}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Status</Label>
                  <p className={colors.textPrimary}>{orgDetail.organization?.subscription_status || 'N/A'}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Users</Label>
                  <p className={colors.textPrimary}>{orgDetail.users?.length || 0}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Created</Label>
                  <p className={colors.textPrimary}>
                    {orgDetail.organization?.created_at ? new Date(orgDetail.organization.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              
              {orgDetail.organization?.stripe_customer_id && (
                <div>
                  <Label className={colors.textMuted}>Stripe Customer</Label>
                  <p className={cn("font-mono text-sm", colors.textPrimary)}>{orgDetail.organization.stripe_customer_id}</p>
                </div>
              )}

              <div className="flex items-center gap-3 pt-3 pb-1 border-t">
                <button
                  data-testid="toggle-analytics-exclusion"
                  type="button"
                  onClick={async () => {
                    const newVal = !orgDetail.organization?.exclude_from_analytics;
                    try {
                      await api.patch(`/api/admin/metrics/organizations/${orgDetail.organization.organization_id}/analytics-exclusion`, { exclude: newVal });
                      setOrgDetail(prev => ({
                        ...prev,
                        organization: { ...prev.organization, exclude_from_analytics: newVal }
                      }));
                      setOrganizations(prev => prev.map(o =>
                        o.organization_id === orgDetail.organization.organization_id
                          ? { ...o, exclude_from_analytics: newVal }
                          : o
                      ));
                      toast.success(newVal ? 'Organization excluded from analytics' : 'Organization included in analytics');
                    } catch {
                      toast.error('Failed to update analytics exclusion');
                    }
                  }}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <div className={cn(
                    "w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
                    orgDetail.organization?.exclude_from_analytics
                      ? "bg-amber-500 border-amber-500"
                      : "border-gray-400"
                  )}>
                    {orgDetail.organization?.exclude_from_analytics && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className={cn("text-sm font-medium", colors.textPrimary)}>Exclude from Analytics</span>
                </button>
                <span className={cn("text-xs", colors.textMuted)}>Hides this org and its users from all analytics metrics</span>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setDetailModal(null); setCloseOrgModal(detailModal); }}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Schedule Closure
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setDetailModal(null); setForceDeleteModal(detailModal); }}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Force Delete
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Close Org Modal */}
      <Dialog open={!!closeOrgModal} onOpenChange={() => { setCloseOrgModal(null); setCloseConfirmName(''); }}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className="text-orange-600">Schedule Organization Closure</DialogTitle>
            <DialogDescription>
              This will schedule the organization for deletion in 30 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className={cn("text-sm", colors.textMuted)}>
              Type <strong>{closeOrgModal?.name}</strong> to confirm
            </p>
            <Input
              value={closeConfirmName}
              onChange={(e) => setCloseConfirmName(e.target.value)}
              placeholder="Organization name"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOrgModal(null)}>Cancel</Button>
            <Button
              onClick={handleAdminCloseOrg}
              disabled={closingOrg || closeConfirmName.toLowerCase().trim() !== closeOrgModal?.name?.toLowerCase().trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {closingOrg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Schedule Closure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Delete Modal */}
      {/* Force Delete Modal */}
      <Dialog open={!!forceDeleteModal} onOpenChange={closeForceDeleteModal}>
        <DialogContent className={cn("max-w-lg max-h-[80vh] overflow-hidden flex flex-col", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {forceDeleteResults ? 'Delete Complete' : 'Force Delete Organization'}
            </DialogTitle>
            {!forceDeleteResults && (
              <DialogDescription>
                This will PERMANENTLY delete all organization data immediately. This cannot be undone.
              </DialogDescription>
            )}
          </DialogHeader>
          
          {!forceDeleteResults ? (
            // Confirmation view
            <div className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700 font-medium">This will delete:</p>
                <ul className="text-sm text-red-600 mt-1 list-disc list-inside">
                  <li>All products, features, epics, goals</li>
                  <li>All users and team members</li>
                  <li>All chat history and files</li>
                  <li>Stripe subscription (if any)</li>
                </ul>
              </div>
              <div>
                <p className={cn("text-sm mb-2", colors.textMuted)}>
                  Type <strong className={colors.textPrimary}>{forceDeleteModal?.name}</strong> to confirm
                </p>
                <Input
                  value={forceDeleteConfirmName}
                  onChange={(e) => setForceDeleteConfirmName(e.target.value)}
                  placeholder="Organization name"
                  className="font-mono"
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={closeForceDeleteModal}>Cancel</Button>
                <Button
                  onClick={handleForceDelete}
                  disabled={forceDeleting || forceDeleteConfirmName.toLowerCase().trim() !== forceDeleteModal?.name?.toLowerCase().trim()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {forceDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Permanently
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Results view
            <div className="flex flex-col flex-1 min-h-0">
              {/* Summary */}
              <div className={cn(
                "p-3 rounded-lg mb-4",
                forceDeleteResults.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              )}>
                <p className={cn(
                  "font-medium",
                  forceDeleteResults.success ? "text-green-700" : "text-red-700"
                )}>
                  {forceDeleteResults.success 
                    ? `✓ ${forceDeleteResults.org_name} deleted successfully`
                    : `✗ Error: ${forceDeleteResults.error || 'Unknown error'}`
                  }
                </p>
                {forceDeleteResults.summary && (
                  <p className={cn("text-sm mt-1", forceDeleteResults.success ? "text-green-600" : "text-red-600")}>
                    {forceDeleteResults.summary.total_collections} collections | {' '}
                    {forceDeleteResults.summary.total_deleted} items deleted | {' '}
                    {forceDeleteResults.summary.errors} errors
                  </p>
                )}
              </div>
              
              {/* Delete Log */}
              {forceDeleteResults.results && (
                <div className="flex-1 overflow-y-auto border rounded-lg">
                  <div className={cn("divide-y", colors.border)}>
                    {forceDeleteResults.results.map((item, idx) => (
                      <div 
                        key={idx} 
                        className={cn(
                          "flex items-center justify-between px-3 py-2 text-sm font-mono",
                          item.status === 'error' && "bg-red-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {getStatusIcon(item.status)}
                          <span className={cn(
                            item.status === 'success' ? colors.textPrimary :
                            item.status === 'error' ? "text-red-600" :
                            colors.textMuted
                          )}>
                            {item.collection}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.status === 'success' && (
                            <span className="text-green-600">{item.count} deleted</span>
                          )}
                          {item.status === 'not_found' && (
                            <span className={colors.textMuted}>0 found</span>
                          )}
                          {item.status === 'error' && (
                            <span className="text-red-600 text-xs">{item.error || 'Error'}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DialogFooter className="mt-4">
                <Button onClick={closeForceDeleteModal}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Tokens Modal */}
      <Dialog open={!!addTokensModal} onOpenChange={() => setAddTokensModal(null)}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle>Add AI Tokens</DialogTitle>
            <DialogDescription>
              Grant additional AI tokens to {addTokensModal?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {orgTokenStatus && (
              <div className={cn("p-3 rounded-lg", colors.bgSecondary)}>
                <p className={cn("text-sm", colors.textMuted)}>Current Balance</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)}>
                  {(orgTokenStatus.tokens_remaining || 0).toLocaleString()} tokens
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Amount to Add</Label>
              <Input
                type="number"
                value={tokenAmount}
                onChange={(e) => setTokenAmount(e.target.value)}
                min="1000"
                step="1000"
              />
              <div className="flex gap-2 flex-wrap">
                {[50000, 100000, 250000, 500000, 1000000].map(amount => (
                  <Button
                    key={amount}
                    variant="outline"
                    size="sm"
                    onClick={() => setTokenAmount(amount.toString())}
                    className={cn("text-xs", tokenAmount === amount.toString() && "border-emerald-500 bg-emerald-50")}
                  >
                    {amount >= 1000000 ? `${amount/1000000}M` : `${amount/1000}K`}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={tokenReason}
                onChange={(e) => setTokenReason(e.target.value)}
                placeholder="e.g., Customer support, Promotion"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTokensModal(null)}>Cancel</Button>
            <Button
              onClick={handleAddTokens}
              disabled={addingTokens || !tokenAmount || parseInt(tokenAmount) <= 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {addingTokens ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" />}
              Add {parseInt(tokenAmount || 0).toLocaleString()} Tokens
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grid Selection Modal */}
      <GridSelectionModal
        isOpen={showGridModal}
        organization={gridModalOrg}
        onClose={() => { setShowGridModal(false); setGridModalOrg(null); }}
        onPlanChanged={handleGridUpdated}
        colors={colors}
      />

      {/* Billing History Modal */}
      <Dialog open={showBillingModal} onOpenChange={closeBillingModal}>
        <DialogContent 
          className={cn(
            "max-w-[80vw] w-[80vw] max-h-[80vh] h-[80vh] p-0 overflow-hidden flex flex-col",
            colors.bgCard
          )}
          hideCloseButton
        >
          <DialogHeader className={cn("px-6 py-4 border-b flex-shrink-0", colors.borderPrimary)}>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className={cn("text-xl font-semibold", colors.textPrimary)}>
                  {billingModalOrg?.name}
                </DialogTitle>
                <p className={cn("text-sm mt-1", colors.textMuted)}>
                  Billing history for {billingModalOrg?.organization_id}
                  {billingModalOrg?.stripe_customer_id && (
                    <span className="ml-2 font-mono text-xs">
                      • Stripe: {billingModalOrg.stripe_customer_id}
                    </span>
                  )}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={closeBillingModal}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto p-6">
            {billingModalOrg && (
              <BillingAuditLog 
                organizationId={billingModalOrg.organization_id} 
                isAdmin={true}
                stripeCustomerId={billingModalOrg.stripe_customer_id}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        onSuccess={fetchOrganizations}
      />
    </div>
  );
};

export default AdminOrganizationsContent;
